import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const Register: React.FC = () => {
  const { register } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch') || 'Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort') || 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const success = await register(
      formData.name,
      formData.firstName,
      formData.email,
      formData.password,
      formData.phone,
      language
    );

    if (success) {
      navigate('/dashboard');
    } else {
      setError(t('auth.registerError') || 'Registration failed. Email may already be in use.');
    }

    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{t('auth.registerTitle') || 'Register'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">{t('auth.name') || 'Name'}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t('auth.namePlaceholder') || 'Enter your name'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="firstName">{t('auth.firstName') || 'First Name'}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder={t('auth.firstNamePlaceholder') || 'Enter your first name'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email') || 'Email'}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('contact.form.email') || 'Your Email'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">{t('auth.phone') || 'Phone'}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder={t('auth.phonePlaceholder') || 'Enter your phone number'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">{t('auth.password') || 'Password'}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              placeholder={t('auth.passwordPlaceholder') || 'Enter your password'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword') || 'Confirm Password'}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              placeholder={t('auth.confirmPasswordPlaceholder') || 'Confirm your password'}
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? (t('auth.loading') || 'Loading...') : (t('auth.register') || 'Register')}
          </button>
        </form>
        <p className="auth-link">
          {t('auth.haveAccount') || 'Already have an account?'}{' '}
          <Link to="/login">{t('auth.login') || 'Login here'}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;

